import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AiSparkleIcon } from "@/components/ai-suggestion/ai-sparkle-icon";

/**
 * Slim one-line banner announcing a batch of applied AI suggestions, with a
 * bulk dismiss action (values are applied by default, so there is nothing to
 * accept). Pure presentation — no tRPC or domain coupling. See
 * docs/ai-suggestion-ui.md for the full pattern and when to use it.
 *
 * `role="status"` makes this an implicit polite live region, so count
 * changes (e.g. after dismissing or editing one suggestion) are announced
 * without any extra `aria-live` wiring.
 */
export interface AiSuggestionBannerProps {
  /** Number of fields currently in suggested state. */
  count: number;
  /**
   * A slower pass is still looking for answers to the remaining questions.
   * Shown even at count 0, since it is the only signal that more may arrive.
   */
  thinking?: boolean;
  onDismissAll: () => void;
  className?: string;
}

export function AiSuggestionBanner({
  count,
  thinking = false,
  onDismissAll,
  className,
}: AiSuggestionBannerProps) {
  const fieldCountLabel = count === 1 ? "field" : "fields";

  return (
    <div
      role="status"
      className={`flex items-center justify-between gap-3 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs sm:text-sm ${className ?? ""}`}
    >
      <span className="flex items-center gap-1.5 text-blue-700">
        {thinking ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <AiSparkleIcon size={16} />
        )}
        {thinking
          ? "Generating more answers…"
          : `${count} ${fieldCountLabel} autofilled with AI`}
      </span>
      {count > 0 && (
        <Button
          type="button"
          variant="link"
          size="none"
          className="h-auto shrink-0 p-0 text-gray-400 hover:text-gray-600"
          aria-label={`Dismiss all ${count} autofilled values`}
          onClick={onDismissAll}
        >
          Dismiss all
        </Button>
      )}
    </div>
  );
}
